
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { BlogPost, BlogPostFormData } from "@/types/blogPost";
import { BlogPostForm } from "./BlogPostForm";
import { BlogPostTable } from "./BlogPostTable";
import { BlogCategoryManager } from "./BlogCategoryManager";
import { Button } from "@/components/ui/button";
import { Plus, Tags } from "lucide-react";
import { fetchBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from "@/utils/blogOperations";

export function BlogPostManager() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const data = await fetchBlogPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar posts.",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (formData: BlogPostFormData) => {
    try {
      if (editingPost) {
        await updateBlogPost(editingPost.id, formData);
        toast({
          title: "Sucesso!",
          description: "Post atualizado com sucesso.",
        });
      } else {
        await createBlogPost(formData);
        toast({
          title: "Sucesso!",
          description: "Post criado com sucesso.",
        });
      }

      setIsEditing(false);
      setEditingPost(null);
      setShowForm(false);
      loadPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      const message = error instanceof Error ? error.message : "Erro ao salvar post.";
      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (postId: string) => {
    if (!confirm("Tem certeza que deseja excluir este post?")) return;

    try {
      await deleteBlogPost(postId);
      toast({
        title: "Sucesso!",
        description: "Post excluído com sucesso.",
      });
      loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir post.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingPost(null);
    setShowForm(false);
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setIsEditing(false);
    setShowForm(true);
  };

  if (showCategories) {
    return (
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button onClick={() => setShowCategories(false)} variant="outline">
            Voltar para Posts
          </Button>
        </div>
        <BlogCategoryManager />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <div className="flex gap-2">
          <Button onClick={() => setShowCategories(true)} variant="outline">
            <Tags className="w-4 h-4 mr-2" />
            Gerenciar Categorias
          </Button>
          {!showForm && (
            <Button onClick={handleNewPost}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Post
            </Button>
          )}
        </div>
      </div>

      {showForm && (
        <BlogPostForm
          isEditing={isEditing}
          editingPost={editingPost}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}
      
      {!showForm && (
        <BlogPostTable
          posts={posts}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
